import React, { useState } from 'react';
import { Spin, Popover } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import WorkOrderLabelChip from './WorkOrderLabelChip';
import { WorkOrderLabel } from '../../data/workOrderStarLabels';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { canAddWOLabelsOrStarNotes } from '../../utils/DataAccessUtils';
import useExclusivePopover from '../../hooks/useExclusivePopover';
import ProUpsellContent from './ProUpsellContent';
import AddLabelChip from './AddLabelChip';

interface Props {
	labels: WorkOrderLabel[];
	maxVisible?: number;
	showAddButton?: boolean;
	/** Figma detail header: hollow pill with white outline on priority-colored background */
	addButtonVariant?: 'default' | 'hollowOnDark';
	/** Row usage: add chip stays hidden until the row (nearest `group` ancestor) is hovered, and fills grey/2 on its own hover. */
	revealAddButtonOnHover?: boolean;
	onAddClick?: (event: React.MouseEvent) => void;
	onChipClick?: (label: WorkOrderLabel) => void;
	onOverflowClick?: (event: React.MouseEvent) => void;
	className?: string;
	loading?: boolean;
	/** `inline` hoists chips into the parent text flow (detail header title row). */
	layout?: 'row' | 'inline';
	/**
	 * Pro entitlement (injected via connect). When false, clicking the add ("+")
	 * chip shows the upgrade-to-Pro upsell instead of opening the assign flow.
	 */
	canUseLabels?: boolean;
}

const SUMMARY_CHIP_BASE_CLASS =
	'footnoteregular box-border h-[30px] cursor-pointer items-center gap-1 rounded-full border border-solid !border-grey-5 bg-white px-2 py-1 text-grey-10';
const SUMMARY_CHIP_CLASS = `inline-flex ${SUMMARY_CHIP_BASE_CLASS}`;

const HOLLOW_ON_DARK_ADD_CHIP_BASE_CLASS =
	'footnoteregular box-border h-[30px] cursor-pointer items-center gap-1 rounded-full border border-solid border-white bg-transparent px-2 py-[3px] text-white';

/** Figma: 12×12 tag icon in summary / add-label pills */
const SUMMARY_TAG_ICON_CLASS =
	'inline-flex h-3 w-3 shrink-0 items-center justify-center text-[12px] leading-none';

const WorkOrderLabelChipsRow: React.FC<Props> = ({
	labels,
	maxVisible = 2,
	showAddButton = false,
	addButtonVariant = 'default',
	revealAddButtonOnHover = false,
	onAddClick,
	onChipClick,
	onOverflowClick,
	className,
	loading = false,
	layout = 'row',
	canUseLabels = true,
}) => {
	const [upsellOpen, setUpsellOpen] = useState(false);
	// Only one floating popover (this upsell, the star-note popover, …) open at a time.
	useExclusivePopover(upsellOpen, () => setUpsellOpen(false));
	if (!labels.length && !showAddButton) return null;
	const visible = labels.slice(0, maxVisible);
	const overflow = labels.length - visible.length;
	const isInline = layout === 'inline';

	const handleSummaryClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		if (onAddClick) onAddClick(event);
	};

	// Add ("+") chip click: non-Pro users get the upgrade upsell instead of the
	// assign-label flow. The button itself stays visible (gated only on click) so
	// the upsell is discoverable, matching the star-with-note Pro gate.
	const handleAddClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		if (!canUseLabels) {
			setUpsellOpen(true);
			return;
		}
		if (onAddClick) onAddClick(event);
	};

	const hollowOnDarkChipClass = `inline-flex ${HOLLOW_ON_DARK_ADD_CHIP_BASE_CLASS}`;
	const hollowOnDarkIconClass = `${SUMMARY_TAG_ICON_CLASS} !text-white`;

	const loadingClass = loading ? 'pointer-events-none opacity-50' : '';

	const renderOverflowChip = (onClick: (event: React.MouseEvent) => void) => (
		<span
			role="button"
			tabIndex={0}
			onClick={onClick}
			className={`${SUMMARY_CHIP_CLASS}${loadingClass ? ` ${loadingClass}` : ''}`}
		>
			<span>{`+ ${overflow}`}</span>
			<TagOutlined size={12} className={SUMMARY_TAG_ICON_CLASS} />
		</span>
	);

	const chips = (
		<>
			{visible.map((label) => (
				<WorkOrderLabelChip
					key={label.id}
					label={label}
					className={loadingClass || undefined}
					onClick={onChipClick ? () => onChipClick(label) : undefined}
				/>
			))}
			{overflow > 0 ? (
				onOverflowClick ? (
					<AccessPermissionChecker
						name={PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS}
						permissionDeniedContent={renderOverflowChip(onOverflowClick)}
					>
						{renderOverflowChip(handleSummaryClick)}
					</AccessPermissionChecker>
				) : (
					renderOverflowChip(handleSummaryClick)
				)
			) : showAddButton ? (
				<AccessPermissionChecker name={PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS}>
					<Popover
						content={
							<ProUpsellContent
								message="Pro users can add labels to work orders"
								onClose={() => setUpsellOpen(false)}
							/>
						}
						trigger={[]}
						visible={upsellOpen}
						onVisibleChange={setUpsellOpen}
						overlayClassName="!w-[400px]"
						placement="bottomLeft"
					>
						{addButtonVariant === 'hollowOnDark' ? (
							<span
								role="button"
								tabIndex={0}
								onClick={handleAddClick}
								className={`${hollowOnDarkChipClass}${loadingClass ? ` ${loadingClass}` : ''}`}
							>
								<span>+</span>
								<TagOutlined size={12} className={hollowOnDarkIconClass} />
							</span>
						) : (
							<AddLabelChip
								onClick={handleAddClick}
								displayClassName={
									revealAddButtonOnHover
										? // `hidden`/`display: none` would pull the trigger out of the tab order
										  // entirely, so keyboard users could never reach it. Opacity keeps it in
										  // the flow (and focusable) at all times, just visually hidden until the
										  // row is hovered OR the trigger itself gains keyboard focus.
										  'inline-flex pointer-events-none opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 focus:pointer-events-auto focus:opacity-100'
										: 'inline-flex'
								}
								disabled={loading}
							/>
						)}
					</Popover>
				</AccessPermissionChecker>
			) : null}
		</>
	);

	// Inline layout: render chips as direct flex children (no Spin/div wrapper — ant-spin-container
	// is block-level and would force chips onto their own row below the title).
	if (isInline) {
		return chips;
	}

	return (
		<Spin spinning={loading}>
			<div
				className={`inline-flex flex-wrap items-center gap-3${className ? ` ${className}` : ''}`}
			>
				{chips}
			</div>
		</Spin>
	);
};

const mapStateToProps = (state) => ({
	canUseLabels: canAddWOLabelsOrStarNotes(state.session.currentUser),
});

export default connect(mapStateToProps)(WorkOrderLabelChipsRow);
